import { offlineDatabase, OfflineReview } from './offlineDatabase';
import { mapRowToCompletedResponse } from './offlineDatabase.mappers';

interface SharedItemLink {
  id: number;
  sourceItemId: number;
  targetItemId: number;
  includeRoom?: boolean;
  includeFloor?: boolean;
  includePhotos?: boolean;
}

interface SharedQuestionMapping {
  sourceQuestionId: string;
  targetQuestionId: string;
  sharedKey: string;
}

interface CompletedSourceResponse {
  id: number;
  reviewId: number;
  itemId: number;
  roomId?: number | null;
  floor?: string | null;
  responseData?: Record<string, any>;
}

interface PrefillMatch {
  preFillData: Record<string, any> | null;
  provenanceData: {
    sourceReviewId: number;
    sourceResponseId: number;
    sharedItemLinkId: number;
    questionMappings: string;
    copiedAt: string;
  } | null;
  roomIdToCopy: number | null;
  floorToCopy: string | null;
  matchedLink: SharedItemLink | null;
  matchedMappings: SharedQuestionMapping[] | null;
  matchedSourceResponse: CompletedSourceResponse | null;
}

interface OfflineSharedPrefillDatabase {
  db?: {
    executeSql: (sql: string, params?: any[]) => Promise<any[]>;
  } | null;
  getRowCount: (tableName: string) => Promise<number>;
  getAllSharedLinksForItems: (itemIds: number[]) => Promise<Map<number, SharedItemLink[]>>;
  getQuestionMappingsForLink: (linkId: number) => Promise<SharedQuestionMapping[]>;
  createResponse: (responseData: any) => Promise<{ id?: number }>;
  getPhotoMetadataByResponseId: (responseId: number) => Promise<any[]>;
}

export interface CreateInitialResponsesInput {
  propertyId: number;
  templateId: number;
  template: any;
  offlineReview: OfflineReview;
}

const escapeRegExp = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const collectTemplateItemIds = (template: any): number[] => {
  const itemIds: number[] = [];

  const traverse = (categories: any[]) => {
    if (!Array.isArray(categories)) {
      return;
    }

    for (const category of categories) {
      if (Array.isArray(category.items)) {
        for (const item of category.items) {
          if (item.id && typeof item.id === 'number') {
            itemIds.push(item.id);
          }
        }
      }

      if (Array.isArray(category.subcategories)) {
        traverse(category.subcategories);
      }
    }
  };

  if (template && Array.isArray(template.categories)) {
    traverse(template.categories);
  }

  return itemIds;
};

export class OfflineSharedPrefillService {
  private sharingLinkCache: Map<number, Map<number, SharedItemLink[]>> = new Map();

  constructor(private database: OfflineSharedPrefillDatabase = offlineDatabase) {}

  clearCache(): void {
    this.sharingLinkCache.clear();
  }

  async createInitialResponses({
    propertyId,
    templateId,
    template,
    offlineReview,
  }: CreateInitialResponsesInput): Promise<void> {
    let sharingLinksByItem = await this.getSharingLinksByItem(templateId, template);
    let responsesByItemId = await this.getCompletedResponsesByItemId(propertyId);

    if (responsesByItemId.size === 0) {
      sharingLinksByItem = new Map();
    }

    if (!Array.isArray(template.categories)) {
      return;
    }

    for (const category of template.categories) {
      if (!Array.isArray(category.items)) {
        continue;
      }

      for (const item of category.items) {
        const match = await this.findPrefillMatch({
          item,
          propertyId,
          sharingLinks: sharingLinksByItem.get(item.id) || [],
          responsesByItemId,
        });

        const createdResponse = await this.database.createResponse({
          reviewId: offlineReview.id,
          tempReviewId: offlineReview.tempId,
          itemId: item.id,
          roomId: match.roomIdToCopy,
          floor: match.floorToCopy,
          isPresent: match.preFillData ? true : item.defaultPresent,
          status: match.preFillData ? 'completed' : 'not_started',
          responseData: match.preFillData || {},
        });

        if (match.provenanceData && createdResponse.id) {
          await this.createProvenanceRecord(createdResponse.id, match.provenanceData);
        }

        if (match.matchedLink?.includePhotos && match.matchedSourceResponse?.id && createdResponse.id) {
          await this.copyPhotos({
            sourceResponseId: match.matchedSourceResponse.id,
            targetResponseId: createdResponse.id,
            mappings: match.matchedMappings,
          });
        }
      }
    }
  }

  private async getSharingLinksByItem(
    templateId: number,
    template: any
  ): Promise<Map<number, SharedItemLink[]>> {
    const cachedLinks = this.sharingLinkCache.get(templateId);
    if (cachedLinks) {
      return cachedLinks;
    }

    const linkCount = await this.database.getRowCount('shared_item_links');

    if (linkCount === 0) {
      return new Map();
    }

    const itemIds = collectTemplateItemIds(template);
    const sharingLinksByItem = await this.database.getAllSharedLinksForItems(itemIds);
    this.sharingLinkCache.set(templateId, sharingLinksByItem);
    return sharingLinksByItem;
  }

  private async getCompletedResponsesByItemId(
    propertyId: number
  ): Promise<Map<number, CompletedSourceResponse>> {
    const completedReviewsResult = await this.database.db!.executeSql(
      `SELECT * FROM reviews
       WHERE property_id = ? AND status IN ('completed', 'submitted')
       ORDER BY COALESCE(completed_at, submitted_at) DESC`,
      [propertyId]
    );

    const completedReviews = completedReviewsResult[0]?.rows.raw() || [];
    if (completedReviews.length === 0) {
      return new Map();
    }

    try {
      const reviewIds = completedReviews.map((review: any) => review.id);
      const placeholders = reviewIds.map(() => '?').join(',');

      const responsesResult = await this.database.db!.executeSql(
        `SELECT * FROM responses
         WHERE review_id IN (${placeholders})
         ORDER BY review_id DESC`,
        reviewIds
      );

      const responsesByItemId = new Map<number, CompletedSourceResponse>();
      const allResponses = responsesResult[0]?.rows.raw() || [];

      for (const responseRow of allResponses) {
        const response = mapRowToCompletedResponse(responseRow);
        if (!responsesByItemId.has(response.itemId)) {
          responsesByItemId.set(response.itemId, response);
        }
      }

      return responsesByItemId;
    } catch (prefetchError) {
      console.error('[OfflineSharedPrefillService] Response prefetch failed:', prefetchError);
      return new Map();
    }
  }

  private async findPrefillMatch({
    item,
    propertyId,
    sharingLinks,
    responsesByItemId,
  }: {
    item: any;
    propertyId: number;
    sharingLinks: SharedItemLink[];
    responsesByItemId: Map<number, CompletedSourceResponse>;
  }): Promise<PrefillMatch> {
    const emptyMatch: PrefillMatch = {
      preFillData: null,
      provenanceData: null,
      roomIdToCopy: null,
      floorToCopy: null,
      matchedLink: null,
      matchedMappings: null,
      matchedSourceResponse: null,
    };

    for (const link of sharingLinks) {
      const isSource = link.sourceItemId === item.id;
      const otherItemId = isSource ? link.targetItemId : link.sourceItemId;
      const sourceResponse = responsesByItemId.get(otherItemId);

      if (!sourceResponse) {
        continue;
      }

      try {
        const mappings = await this.database.getQuestionMappingsForLink(link.id);

        if (mappings.length === 0) {
          continue;
        }

        const questionData = this.buildMappedQuestionData({
          isSource,
          sourceData: sourceResponse.responseData || {},
          mappings,
          includeRoom: link.includeRoom,
          includeFloor: link.includeFloor,
        });

        const roomIdToCopy = link.includeRoom
          ? await this.resolveRoomIdToCopy(sourceResponse.roomId, propertyId)
          : null;
        const floorToCopy = link.includeFloor && sourceResponse.floor
          ? sourceResponse.floor
          : null;

        if (questionData.preFillData || roomIdToCopy || floorToCopy) {
          return {
            preFillData: questionData.preFillData,
            provenanceData: questionData.preFillData
              ? {
                  sourceReviewId: sourceResponse.reviewId,
                  sourceResponseId: sourceResponse.id,
                  sharedItemLinkId: link.id,
                  questionMappings: JSON.stringify(questionData.mappingsUsed),
                  copiedAt: new Date().toISOString(),
                }
              : null,
            roomIdToCopy,
            floorToCopy,
            matchedLink: link,
            matchedMappings: mappings,
            matchedSourceResponse: sourceResponse,
          };
        }
      } catch (mappingError) {
        console.error(`[OfflineSharedPrefillService] Error processing link ${link.id}:`, mappingError);
      }
    }

    return emptyMatch;
  }

  private buildMappedQuestionData({
    isSource,
    sourceData,
    mappings,
    includeRoom,
    includeFloor,
  }: {
    isSource: boolean;
    sourceData: Record<string, any>;
    mappings: SharedQuestionMapping[];
    includeRoom?: boolean;
    includeFloor?: boolean;
  }): { preFillData: Record<string, any> | null; mappingsUsed: Record<string, string> } {
    const preFillData: Record<string, any> = {};
    const mappingsUsed: Record<string, string> = {};

    for (const mapping of mappings) {
      const srcQuestionId = isSource ? mapping.targetQuestionId : mapping.sourceQuestionId;
      const tgtQuestionId = isSource ? mapping.sourceQuestionId : mapping.targetQuestionId;

      const sourceValue = sourceData[srcQuestionId];
      if (sourceValue !== undefined && sourceValue !== null) {
        preFillData[tgtQuestionId] = sourceValue;
        mappingsUsed[tgtQuestionId] = mapping.sharedKey;
      }

      const suffixPattern = new RegExp(`^${escapeRegExp(srcQuestionId)}_item_(\\d+)$`);
      Object.keys(sourceData).forEach(key => {
        const match = key.match(suffixPattern);
        if (match) {
          const itemNumber = match[1];
          const targetSuffixedKey = `${tgtQuestionId}_item_${itemNumber}`;
          preFillData[targetSuffixedKey] = sourceData[key];
          mappingsUsed[targetSuffixedKey] = mapping.sharedKey;
        }
      });
    }

    if (includeRoom) {
      Object.keys(sourceData).forEach(key => {
        if (/^roomId_item_(\d+)$/.test(key)) {
          preFillData[key] = sourceData[key];
        }
      });
    }

    if (includeFloor) {
      Object.keys(sourceData).forEach(key => {
        if (/^floor_item_(\d+)$/.test(key)) {
          preFillData[key] = sourceData[key];
        }
      });
    }

    if (Object.keys(preFillData).length === 0) {
      return { preFillData: null, mappingsUsed };
    }

    return { preFillData, mappingsUsed };
  }

  private async resolveRoomIdToCopy(
    sourceRoomId: number | null | undefined,
    propertyId: number
  ): Promise<number | null> {
    if (!sourceRoomId) {
      return null;
    }

    try {
      const roomResult = await this.database.db!.executeSql(
        'SELECT * FROM rooms_cache WHERE id = ?',
        [sourceRoomId]
      );
      const sourceRoom = roomResult[0]?.rows.item(0);

      if (!sourceRoom) {
        return sourceRoomId;
      }

      if (sourceRoom.is_custom && sourceRoom.property_id !== propertyId) {
        const targetRoomResult = await this.database.db!.executeSql(
          'SELECT * FROM rooms_cache WHERE property_id = ? AND name = ?',
          [propertyId, sourceRoom.name]
        );
        const targetRoom = targetRoomResult[0]?.rows.item(0);

        return targetRoom ? targetRoom.id : null;
      }

      return sourceRoomId;
    } catch {
      return sourceRoomId;
    }
  }

  private async createProvenanceRecord(
    responseId: number,
    provenanceData: NonNullable<PrefillMatch['provenanceData']>
  ): Promise<void> {
    await this.database.db!.executeSql(
      `INSERT INTO review_response_provenance
       (response_id, source_review_id, source_response_id, shared_item_link_id, question_mappings, copied_at, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?)`,
      [
        responseId,
        provenanceData.sourceReviewId,
        provenanceData.sourceResponseId,
        provenanceData.sharedItemLinkId,
        provenanceData.questionMappings,
        provenanceData.copiedAt,
        new Date().toISOString(),
      ]
    );
  }

  private async copyPhotos({
    sourceResponseId,
    targetResponseId,
    mappings,
  }: {
    sourceResponseId: number;
    targetResponseId: number;
    mappings: SharedQuestionMapping[] | null;
  }): Promise<void> {
    try {
      const sourcePhotos = await this.database.getPhotoMetadataByResponseId(sourceResponseId);

      for (const sourcePhoto of sourcePhotos) {
        try {
          let targetQuestionId = sourcePhoto.question_id;

          if (sourcePhoto.question_id && mappings && mappings.length > 0) {
            const mapping = mappings.find(
              item => item.sourceQuestionId === sourcePhoto.question_id
            );
            if (mapping) {
              targetQuestionId = mapping.targetQuestionId;
            }
          }

          await this.database.db!.executeSql(
            `INSERT INTO photos_metadata (
              response_id, s3_key, s3_url, file_name, mime_type, size,
              caption, note, metadata, question_id, uploaded_at, cached_at
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              targetResponseId,
              sourcePhoto.s3_key,
              sourcePhoto.s3_url,
              sourcePhoto.file_name,
              sourcePhoto.mime_type || 'image/jpeg',
              sourcePhoto.size || 0,
              sourcePhoto.caption || null,
              sourcePhoto.note || null,
              sourcePhoto.metadata || null,
              targetQuestionId,
              sourcePhoto.uploaded_at || new Date().toISOString(),
              sourcePhoto.cached_at || new Date().toISOString(),
            ]
          );
        } catch (photoInsertError) {
          console.warn(
            `[OfflineSharedPrefillService] Failed to copy photo ${sourcePhoto.id}:`,
            photoInsertError
          );
        }
      }
    } catch (photoError) {
      console.warn('[OfflineSharedPrefillService] Failed to copy photos:', photoError);
    }
  }
}
